import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Search,
  RefreshCw,
  Replace,
  Trash2,
  Info,
  X,
  FileText,
  LayoutTemplate,
  ImageOff,
  Loader2
} from 'lucide-react';
import MuscleBadges from '../common/MuscleBadges';

const ExerciseNoStaticImagePanel = ({ onRefresh }) => {
  const { getAuthenticatedApi } = useAuth();
  const api = getAuthenticatedApi();

  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [usageByExerciseId, setUsageByExerciseId] = useState({});
  const [usageDialogExercise, setUsageDialogExercise] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [replacingExerciseId, setReplacingExerciseId] = useState(null);
  const [replaceSearchQuery, setReplaceSearchQuery] = useState('');
  const [replaceExercises, setReplaceExercises] = useState([]);
  const [replaceLoading, setReplaceLoading] = useState(false);

  const fetchExercises = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/exercises_without_static_image.php');
      if (response.data?.success) {
        const list = response.data.exercises || [];
        setExercises(list);
        if (list.length > 0) {
          const ids = list.map((ex) => ex.id).join(',');
          const usageRes = await api.get('/exercise_usage_api.php', { params: { exercise_ids: ids } });
          if (usageRes.data?.success && usageRes.data?.usage) {
            setUsageByExerciseId(usageRes.data.usage);
          }
        } else {
          setUsageByExerciseId({});
        }
      } else {
        setError(response.data?.message || 'Errore nel recupero degli esercizi');
      }
    } catch (err) {
      console.error('Errore esercizi senza immagine statica:', err);
      setError('Errore di connessione al server');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExercises();
  }, []);

  const searchReplaceExercises = async (query) => {
    if (!query || query.trim() === '') {
      setReplaceExercises([]);
      return;
    }
    try {
      setReplaceLoading(true);
      const response = await api.get('/esercizi.php', {
        params: { search: query.trim(), limit: 20, include_muscles: true }
      });
      const approved = Array.isArray(response.data)
        ? response.data.filter((ex) => !ex.status || ex.status === 'approved')
        : [];
      const filtered = approved.filter((ex) => ex.id !== replacingExerciseId);
      setReplaceExercises(filtered);
    } catch (err) {
      console.error('Errore ricerca esercizi:', err);
      setReplaceExercises([]);
    } finally {
      setReplaceLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => {
      if (replacingExerciseId) searchReplaceExercises(replaceSearchQuery);
    }, 300);
    return () => clearTimeout(t);
  }, [replaceSearchQuery, replacingExerciseId]);

  const handleReplace = async (newExerciseId) => {
    if (!replacingExerciseId || !newExerciseId) return;
    try {
      setLoading(true);
      setError(null);
      const response = await api.post('/admin_replace_exercise_in_workouts.php', {
        old_exercise_id: replacingExerciseId,
        new_exercise_id: newExerciseId
      });
      if (response.data?.success) {
        setMessage({ type: 'success', text: response.data.message || 'Esercizio sostituito con successo.' });
        setTimeout(() => setMessage(null), 5000);
        setReplacingExerciseId(null);
        setReplaceSearchQuery('');
        setReplaceExercises([]);
        fetchExercises();
        if (onRefresh) onRefresh();
      } else {
        setError(response.data?.message || 'Errore durante la sostituzione');
      }
    } catch (err) {
      console.error('Errore sostituzione:', err);
      setError(err.response?.data?.message || 'Errore di connessione');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      setError(null);
      await api.delete(`/esercizi.php?id=${id}`);
      setConfirmDeleteId(null);
      setMessage({ type: 'success', text: 'Esercizio eliminato.' });
      setTimeout(() => setMessage(null), 3000);
      setExercises((prev) => prev.filter((ex) => ex.id !== id));
      setUsageByExerciseId((prev) => {
        const next = { ...prev };
        delete next[String(id)];
        return next;
      });
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Errore eliminazione:', err);
      setError(err.response?.data?.message || 'Errore eliminazione');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow-md">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <ImageOff className="h-6 w-6 text-amber-500" />
          Esercizi senza immagine statica
        </h2>
        <button
          onClick={fetchExercises}
          className="flex items-center px-3 py-2 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-800/50 transition"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Aggiorna
        </button>
      </div>

      <div className="p-4 mb-6 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 rounded-lg flex items-start gap-2">
        <Info className="h-5 w-5 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <p>Questi esercizi non hanno <strong>immagine_statica</strong> (esercizi vecchi).</p>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>Se <strong>non sono usati</strong> in nessuna scheda/template puoi <strong>eliminarli</strong>.</li>
            <li>Se sono usati in schede o template, <strong>sostituiscili</strong> con un esercizio nuovo (con immagine statica).</li>
          </ul>
        </div>
      </div>

      {message && (
        <div
          className={`mb-4 p-3 rounded-lg ${
            message.type === 'success'
              ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800'
              : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'
          }`}
        >
          <p>{message.text}</p>
        </div>
      )}
      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800 rounded-lg">
          <p>{error}</p>
        </div>
      )}

      {loading && exercises.length === 0 ? (
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-12 w-12 animate-spin text-blue-500" />
        </div>
      ) : exercises.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <ImageOff className="h-16 w-16 mx-auto mb-4 opacity-50" />
          <p>Nessun esercizio senza immagine statica.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {exercises.map((exercise) => {
            const usage = usageByExerciseId[String(exercise.id)];
            const countS = usage?.count_schede ?? 0;
            const countT = usage?.count_templates ?? 0;
            const countTStandalone = usage?.count_templates_standalone ?? 0;
            const canDelete = countS === 0 && countT === 0 && countTStandalone === 0;

            return (
              <div
                key={exercise.id}
                className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-amber-50/30 dark:bg-amber-900/10"
              >
                <div className="p-4 border-b border-amber-200/50 dark:border-amber-800/50">
                  <div className="flex justify-between items-start gap-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{exercise.nome}</h3>
                      <div className="mt-2">
                        <MuscleBadges
                          primaryMuscle={exercise.primary_muscle}
                          secondaryMuscles={exercise.secondary_muscles}
                          variant="compact"
                        />
                      </div>
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-gray-100 dark:bg-gray-700 text-sm text-gray-600 dark:text-gray-400">
                          <FileText className="h-3.5 w-3.5" />
                          Schede: {countS}
                        </span>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-gray-100 dark:bg-gray-700 text-sm text-gray-600 dark:text-gray-400">
                          <LayoutTemplate className="h-3.5 w-3.5" />
                          Template: {countT + countTStandalone}
                        </span>
                        <button
                          type="button"
                          onClick={() => setUsageDialogExercise(exercise)}
                          className="inline-flex items-center justify-center w-7 h-7 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                          title="Dove è utilizzato"
                        >
                          <Info className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
                      <button
                        onClick={() => setReplacingExerciseId(exercise.id)}
                        className="p-1.5 text-white bg-purple-600 hover:bg-purple-700 rounded transition-colors inline-flex items-center justify-center"
                        title="Sostituisci con altro esercizio"
                      >
                        <Replace className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => setConfirmDeleteId(exercise.id)}
                        disabled={!canDelete}
                        className={`p-1.5 rounded transition-colors inline-flex items-center justify-center ${
                          canDelete
                            ? 'text-white bg-red-600 hover:bg-red-700'
                            : 'bg-gray-300 dark:bg-gray-600 text-gray-500 cursor-not-allowed'
                        }`}
                        title={canDelete ? 'Elimina (non usato in schede/template)' : 'Elimina solo se non usato'}
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-white dark:bg-gray-800">
                  {exercise.descrizione && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{exercise.descrizione}</p>
                  )}
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Attrezzatura: {exercise.attrezzatura || 'Nessuna'}
                  </div>

                  {confirmDeleteId === exercise.id && (
                    <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                      <p className="text-sm text-red-700 dark:text-red-300 mb-2">
                        Eliminare questo esercizio? Non è usato in nessuna scheda né template.
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setConfirmDeleteId(null)}
                          className="px-3 py-1 text-sm bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded"
                        >
                          Annulla
                        </button>
                        <button
                          onClick={() => handleDelete(exercise.id)}
                          className="px-3 py-1 text-sm bg-red-600 hover:bg-red-700 text-white rounded"
                        >
                          Conferma eliminazione
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal "Dove è utilizzato" */}
      {usageDialogExercise && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={() => setUsageDialogExercise(null)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-md w-full max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <Info className="h-5 w-5 text-indigo-500" />
                Dove è utilizzato
              </h3>
              <button
                type="button"
                onClick={() => setUsageDialogExercise(null)}
                className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
                aria-label="Chiudi"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="p-4 flex-1 overflow-y-auto">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">{usageDialogExercise.nome}</p>
              {(() => {
                const usage = usageByExerciseId[String(usageDialogExercise.id)];
                if (!usage) return <p className="text-sm text-gray-500">Caricamento...</p>;
                const { schede, templates, templates_standalone } = usage;
                const noUsage = (!schede || schede.length === 0) && (!templates || templates.length === 0) && (!templates_standalone || templates_standalone.length === 0);
                if (noUsage) return <p className="text-sm text-gray-500">Non utilizzato in nessuna scheda né template.</p>;
                return (
                  <div className="space-y-4">
                    {schede?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2 mb-2">
                          <FileText className="h-4 w-4" />
                          Schede ({schede.length})
                        </h4>
                        <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600 dark:text-gray-400">
                          {schede.map((s) => (
                            <li key={s.id}>
                              {s.nome}
                              {s.assignee && <span className="text-gray-500"> - {s.assignee}</span>}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {templates?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2 mb-2">
                          <LayoutTemplate className="h-4 w-4" />
                          Template palestra ({templates.length})
                        </h4>
                        <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600 dark:text-gray-400">
                          {templates.map((t) => (
                            <li key={t.id}>
                              {t.name}
                              {t.gym_name && <span className="text-gray-500"> - {t.gym_name}</span>}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {templates_standalone?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2 mb-2">
                          <LayoutTemplate className="h-4 w-4" />
                          Template standalone ({templates_standalone.length})
                        </h4>
                        <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600 dark:text-gray-400">
                          {templates_standalone.map((t) => (
                            <li key={t.id}>{t.name}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Modal Sostituisci */}
      {replacingExerciseId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-2xl w-full max-h-[80vh] flex flex-col">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">Sostituisci esercizio</h3>
                <button
                  onClick={() => {
                    setReplacingExerciseId(null);
                    setReplaceSearchQuery('');
                    setReplaceExercises([]);
                  }}
                  className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                  aria-label="Chiudi"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Cerca e seleziona l&apos;esercizio nuovo (con immagine statica) con cui sostituire questo esercizio in tutte le schede e i template.
              </p>
            </div>
            <div className="p-6 flex-1 overflow-y-auto">
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  value={replaceSearchQuery}
                  onChange={(e) => setReplaceSearchQuery(e.target.value)}
                  placeholder="Cerca esercizio per nome..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  autoFocus
                />
              </div>
              {replaceLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                </div>
              ) : replaceExercises.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  {replaceSearchQuery ? 'Nessun esercizio trovato.' : 'Digita per cercare...'}
                </div>
              ) : (
                <div className="space-y-2">
                  {replaceExercises.map((ex) => (
                    <div
                      key={ex.id}
                      onClick={() => handleReplace(ex.id)}
                      className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                    >
                      <div className="flex justify-between items-start gap-4">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-gray-900 dark:text-white">{ex.nome}</h4>
                          {ex.descrizione && (
                            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{ex.descrizione}</p>
                          )}
                          <div className="mt-2">
                            <MuscleBadges
                              primaryMuscle={ex.primary_muscle}
                              secondaryMuscles={ex.secondary_muscles}
                              variant="compact"
                            />
                          </div>
                        </div>
                        <span className="flex-shrink-0 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm">
                          Sostituisci
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExerciseNoStaticImagePanel;
